import { FlashList } from "@shopify/flash-list";
import { AHSeparator, ListItem, SectionHeader } from "arkh";
import React, { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useListContentStyle } from "../../../../components/mobile/useListContentStyle";
import { RoutineEditScreenProps } from "../../RoutineEditMain/RoutineEditNavigation";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../../State/RoutineEditState";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { BasicTaskItem } from "../BasicTaskItem";

/*

Ability to add tasks that already exist in other sections but not in this one.
Only show tasks that are not already in this section.
*/
export default function RoutineEditChecklistSectionAddExistingTasks(
  props: RoutineEditScreenProps<"RoutineEditChecklistSectionAddExistingTasks">
) {
  const { tasks = [], connections = [] } = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();
  const sectionId = props.route.params.sectionId;

  const [toAdd, setToAdd] = useState<string[]>([]);
  const tasksThatCanBeAdded = useMemo(() => {
    return tasks.flatMap((_task) => {
      const hasConnection =
        connections.findIndex(
          (connection) =>
            connection.taskId === _task.id && connection.sectionId === sectionId
        ) !== -1;
      return hasConnection ? [] : _task;
    });
  }, [connections, sectionId, tasks]);

  const listContentStyle = useListContentStyle();

  function applyChanges() {
    setParams({
      connections: [
        ...connections,
        ...toAdd.map((taskId, i) => ({
          ordinal: connections.length + i,
          taskId,
          sectionId: props.route.params.sectionId,
          value: 0,
          orgTopic: null,
        })),
      ],
    });
  }

  const intl = useIntl();

  useNavigationHeader({
    type: "DONE",
    title: intl.formatMessage({
      defaultMessage: "Add existing tasks",
      description: "RoutineEditChecklistSectionAddExistingTasks title",
    }),
    defaultContentStyle: true,
    onDone: () => {
      applyChanges();
      props.navigation.goBack();
    },
  });

  return (
    <FlashList
      ListHeaderComponent={
        <SectionHeader>
          <SectionHeader.Title>
            <FormattedMessage
              defaultMessage="Add existing tasks"
              description="RoutineEditChecklistSectionAddExistingTasks title"
            />
          </SectionHeader.Title>
          <SectionHeader.Description>
            <FormattedMessage
              defaultMessage="Select the tasks you want to add to the section."
              description="RoutineEditChecklistSectionAddExistingTasks description"
            />
          </SectionHeader.Description>
        </SectionHeader>
      }
      contentContainerStyle={listContentStyle}
      keyExtractor={(item) => item.id}
      data={tasksThatCanBeAdded}
      ItemSeparatorComponent={AHSeparator}
      renderItem={({ item, index }) => {
        const selected = toAdd.includes(item.id);
        return (
          <ListItem
            border="border-primary"
            onPress={() => {
              setToAdd((prev) =>
                selected
                  ? prev.filter((id) => id !== item.id)
                  : [...prev, item.id]
              );
            }}
            isFirst={index === 0}
            isLast={index === tasksThatCanBeAdded.length - 1}
            right={<ListItem.MultiSelectIndicator selected={selected} />}
          >
            <BasicTaskItem task={item} />
          </ListItem>
        );
      }}
    />
  );
}
